import { API_BASE } from './api-base';
import type { BillingLane, ScaStage } from './upgrade-confirmation';

/**
 * A second, server-side destination for the SCA stages `#8145` already reports as BI.
 *
 * The BI events are the product's analytics record and stay as they are. This sink exists because
 * that record is currently UNREADABLE to us: `bi_events_raw` projects exactly four dimension keys
 * for src 198 (`mcp_name`, `function_name`, `error_types`, `error_msg`) and none of them is
 * `componentName` or `additionalFields`, so the stage detail is absent from that datasource by
 * construction rather than missing from a query. `app_logs` is the one place these can be read
 * today, so the stages are posted to a route that logs them there.
 *
 * What it is for, precisely: across 11 live holds (2026-08-09→15) the client's abandon path fired
 * ZERO times, so no live challenge ever returned a definitive verdict, while sandbox fires it in
 * seconds. `challenge-invoked` present with no follow-up stage means the browser called Stripe and
 * never got an answer; absent means it never called at all. One `app_logs` query then separates
 * those, which is the question the whole investigation is stuck on.
 *
 * Delete this when the BI stages become readable — it is a debug channel, not a second analytics
 * system.
 */

/**
 * The stages the route will accept. Typed as `Record<ScaStage, true>`, so adding a stage to
 * `ScaStage` without adding it here fails to compile — the allowlist cannot silently drift behind
 * the reporter and start dropping the stage nobody notices is missing.
 *
 * It is an allowlist rather than a length check because the value is written into a log line: an
 * unconstrained string from a browser is log injection, and every legitimate value is one of these.
 */
const STAGES: Record<ScaStage, true> = {
  'no-confirmable-response': true,
  'stripe-unavailable': true,
  'challenge-invoked': true,
  'challenge-error': true,
  'challenge-status': true,
  'challenge-threw': true,
  'intent-reread': true,
  verdict: true,
  abandon: true,
};

export function isScaStage(value: unknown): value is ScaStage {
  return typeof value === 'string' && Object.prototype.hasOwnProperty.call(STAGES, value);
}

/**
 * Reduce a free-text field to something safe to concatenate into a log line. Details are Stripe's
 * own vocabulary (`invalid_request_error/payment_intent_authentication_failure`, `requires_action`,
 * `bad-lane:none`), so the permitted set covers every real value; anything else is dropped rather
 * than escaped, because a detail we don't recognize has no diagnostic value anyway.
 */
export function sanitizeDetail(value: unknown, maxLength = 80): string {
  if (typeof value !== 'string') return '';
  return value.replace(/[^A-Za-z0-9._:/-]/g, '').slice(0, maxLength);
}

/** Stripe ids are `[A-Za-z0-9_]` — same reasoning as `sanitizeDetail`. */
export function sanitizeStripeId(value: unknown, maxLength = 64): string {
  if (typeof value !== 'string') return '';
  return value.replace(/[^A-Za-z0-9_]/g, '').slice(0, maxLength);
}

export interface ScaStagePayload {
  stage: ScaStage;
  detail?: string;
  subscriptionId?: string;
  billingLane?: BillingLane;
}

/**
 * Post one stage. Fire-and-forget and never awaited, for the same reason the BI reporter isn't:
 * this runs inside a live payment confirmation, and instrumentation must never be able to delay or
 * change a money verdict. Every failure is swallowed — a lost stage costs a diagnostic, a thrown
 * one could cost a buyer their upgrade.
 *
 * `keepalive` matters here specifically: the case being measured is a challenge whose page goes
 * away, and without it the browser cancels in-flight requests on unload — losing exactly the
 * stages that would prove it.
 */
export function postScaStage(payload: ScaStagePayload): void {
  try {
    void fetch(`${API_BASE}/api/premium/sca-stage`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      keepalive: true,
      body: JSON.stringify(payload),
    }).catch(() => {
      /* diagnostics are best-effort by construction */
    });
  } catch {
    /* no fetch, no window, a serialization failure — none of it may reach the caller */
  }
}
